import React, { useState, useMemo } from 'react';
import { CheckCircle2, Clock, AlertTriangle, XCircle } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';

// Polymet 3-tab UX, but the Pending tab surfaces sub-rows for the three
// underlying states (EOI pending, NDA awaiting buyer, NDA awaiting seller).
const TABS = [
  { key: 'pending',  label: 'PENDING' },
  { key: 'approved', label: 'APPROVED' },
  { key: 'declined', label: 'DECLINED' },
];

const INDUSTRY_LABELS = {
  trades_construction:   'Trades & Construction',
  manufacturing:         'Manufacturing',
  professional_services: 'Professional Services',
  retail:                'Retail',
  technology:            'Technology',
};

function bucketFor(status) {
  if (['pending', 'matched', 'nda_issued'].includes(status)) return 'pending';
  if (status === 'accepted') return 'approved';
  return 'declined'; // declined / cancelled / expired / completed
}

// Render the Pending tab as 3 distinct sub-states. The "your signature
// needed" sub-state is the actionable queue.
function pendingSubState(inquiry) {
  if (inquiry.status === 'pending') return 'eoi_pending';
  if (inquiry.status === 'nda_issued' && !inquiry.nda_buyer_signed_at) return 'nda_awaiting_buyer';
  if (inquiry.status === 'nda_issued' && inquiry.nda_buyer_signed_at)  return 'nda_awaiting_seller';
  return 'eoi_pending';
}

const SUB_STATE_BADGE = {
  eoi_pending:        { label: 'EOI Pending',                  className: 'bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/30 text-[hsl(var(--succeedo-orange))]' },
  nda_awaiting_buyer: { label: 'NDA — Awaiting Buyer',         className: 'bg-muted border border-border text-muted-foreground' },
  nda_awaiting_seller:{ label: 'YOUR SIGNATURE NEEDED',         className: 'bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/40 text-[hsl(var(--succeedo-orange))] font-semibold' },
};

const STATUS_BADGE = {
  approved: { label: 'APPROVED', className: 'bg-[hsl(var(--chart-2))]/10 border border-[hsl(var(--chart-2))]/30 text-[hsl(var(--chart-2))]' },
  declined: { label: 'DECLINED', className: 'bg-destructive/10 border border-destructive/30 text-destructive' },
};

function timeAgo(isoString) {
  if (!isoString) return '';
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 60)        return 'just now';
  if (seconds < 3600)      return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400)    return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) === 1 ? '' : 's'} ago`;
  return `${Math.floor(seconds / 86_400)} day${Math.floor(seconds / 86_400) === 1 ? '' : 's'} ago`;
}

export default function DealRoomInquiriesPage({
  inquiries = [],
  loading = false,
  approving = {},  // { [inquiryId]: bool }
  declining = {},
  signing  = {},
  persona = 'SELLER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
  onApprove,    // (inquiryId) => Promise
  onDecline,    // (inquiryId) => Promise
  onSignNda,    // (inquiryId) => Promise
}) {
  const [activeTab, setActiveTab] = useState('pending');

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, declined: 0, all: inquiries.length };
    inquiries.forEach((i) => { c[bucketFor(i.status)] = (c[bucketFor(i.status)] || 0) + 1; });
    return c;
  }, [inquiries]);

  const filtered = useMemo(() => (
    inquiries.filter((i) => bucketFor(i.status) === activeTab)
  ), [inquiries, activeTab]);

  return (
    <AppLayout persona={persona} userName={userName} userInitials={userInitials}
               planProgress={planProgress} actionPlanBadge={actionPlanBadge}>
      <div className="max-w-[1200px] mx-auto px-6 py-12">
        <header className="flex items-center gap-2 mb-8">
          <span className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" aria-hidden="true" />
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            BUYER INQUIRIES
          </span>
        </header>

        <h1 className="font-serif text-[42px] font-light text-foreground tracking-[-0.02em] mb-6">
          Buyer Inquiries
        </h1>

        <nav className="flex items-center gap-2 mb-6" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              onClick={() => setActiveTab(t.key)}
              className={`font-mono text-[11px] uppercase px-4 py-2 rounded-full transition-colors ${
                activeTab === t.key
                  ? 'bg-foreground text-background'
                  : 'border border-border text-muted-foreground hover:border-border/50'
              }`}
            >
              {t.label} ({counts[t.key] || 0})
            </button>
          ))}
        </nav>

        {loading && filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">Loading inquiries…</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center">No inquiries in this tab</p>
        ) : (
          <ul className="space-y-4">
            {filtered.map((inquiry) => (
              <InquiryCard
                key={inquiry.id}
                inquiry={inquiry}
                bucket={activeTab}
                approving={approving[inquiry.id]}
                declining={declining[inquiry.id]}
                signing={signing[inquiry.id]}
                onApprove={() => onApprove?.(inquiry.id)}
                onDecline={() => onDecline?.(inquiry.id)}
                onSignNda={() => onSignNda?.(inquiry.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </AppLayout>
  );
}

function InquiryCard({ inquiry, bucket, approving, declining, signing, onApprove, onDecline, onSignNda }) {
  const initials = inquiry.buyer_initials || '??';
  const buyerName = inquiry.buyer_name || 'Buyer';
  const province = inquiry.buyer_province || '—';
  const message = inquiry.interest_message || 'No message provided';
  const dealLabel = inquiry.deal_headline || INDUSTRY_LABELS[inquiry.deal_industry] || '—';

  return (
    <li className="bg-card border border-border rounded-[16px] p-5 hover:border-border/50 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
          <span className="text-foreground font-semibold text-[15px]">{initials}</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div>
              <h3 className="text-[15px] font-semibold text-foreground mb-1">{buyerName}</h3>
              <p className="font-mono text-[10px] uppercase text-muted-foreground/60">
                BUYER · {province} · on {dealLabel}
              </p>
            </div>
            <BucketBadge inquiry={inquiry} bucket={bucket} />
          </div>

          <p className="text-[14px] text-muted-foreground/80 line-clamp-3 mb-4">{message}</p>

          {bucket === 'pending' && (
            <PendingActions
              inquiry={inquiry}
              approving={approving}
              declining={declining}
              signing={signing}
              onApprove={onApprove}
              onDecline={onDecline}
              onSignNda={onSignNda}
            />
          )}

          {bucket === 'approved' && (
            <p className="text-[13px] text-muted-foreground">
              <CheckCircle2 className="inline w-4 h-4 text-[hsl(var(--chart-2))] mr-1.5 -mt-0.5" />
              NDA active · {inquiry.accepted_at ? timeAgo(inquiry.accepted_at) : 'just approved'}
            </p>
          )}

          {bucket === 'declined' && (
            <p className="text-[13px] text-muted-foreground/60">
              <XCircle className="inline w-4 h-4 mr-1.5 -mt-0.5" />
              Declined {timeAgo(inquiry.created_at)}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

function BucketBadge({ inquiry, bucket }) {
  if (bucket === 'pending') {
    const sub = pendingSubState(inquiry);
    const cfg = SUB_STATE_BADGE[sub];
    return (
      <span className={`inline-block font-mono text-[10px] uppercase rounded-full px-3 py-1 ${cfg.className}`}>
        {cfg.label}
      </span>
    );
  }
  const cfg = STATUS_BADGE[bucket];
  if (!cfg) return null;
  return (
    <span className={`inline-block font-mono text-[10px] uppercase rounded-full px-3 py-1 ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function PendingActions({ inquiry, approving, declining, signing, onApprove, onDecline, onSignNda }) {
  const sub = pendingSubState(inquiry);

  if (sub === 'eoi_pending') {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onApprove}
          disabled={approving}
          className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-2 text-sm hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors disabled:opacity-50"
        >
          {approving ? 'Approving…' : 'Send NDA'}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={declining}
          className="border border-border text-muted-foreground rounded-full px-4 py-2 text-sm hover:bg-accent transition-colors disabled:opacity-50"
        >
          {declining ? 'Declining…' : 'Decline'}
        </button>
      </div>
    );
  }

  if (sub === 'nda_awaiting_buyer') {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-[13px] text-muted-foreground">
          <Clock className="inline w-4 h-4 mr-1.5 -mt-0.5" />
          Buyer reviewing NDA — issued {timeAgo(inquiry.nda_issued_at)}
        </p>
        <button
          type="button"
          onClick={onDecline}
          disabled={declining}
          className="border border-border text-muted-foreground rounded-full px-4 py-2 text-sm hover:bg-accent transition-colors disabled:opacity-50 ml-auto"
        >
          Withdraw
        </button>
      </div>
    );
  }

  if (sub === 'nda_awaiting_seller') {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-[13px] text-foreground flex-1">
          <AlertTriangle className="inline w-4 h-4 text-[hsl(var(--succeedo-orange))] mr-1.5 -mt-0.5" />
          Buyer signed {timeAgo(inquiry.nda_buyer_signed_at)} — your signature finalizes approval.
        </p>
        <button
          type="button"
          onClick={onSignNda}
          disabled={signing}
          className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-2 text-sm hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors disabled:opacity-50"
        >
          {signing ? 'Signing…' : 'Sign & Approve'}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={declining}
          className="border border-border text-muted-foreground rounded-full px-4 py-2 text-sm hover:bg-accent transition-colors disabled:opacity-50"
        >
          Abort
        </button>
      </div>
    );
  }

  return null;
}
